import React, { useEffect, useState } from "react";
import styled from "styled-components";
import "./home.css";
import logo from "../images/logo.png";
import PhotoSlider from "../Components/PhotoSlider";
import { motion } from "framer-motion";

const sections = [
  {
    title: "Couples",
    text: "I capture the raw, intimate moments that define your connection—whether it’s laughter, gentle touches, or stolen glances. Every couple has a story, and my goal is to preserve it in a way that feels authentic and timeless, creating images that reflect the love you share.",
    reverse: true,
  },
  {
    title: "Lifestyle",
    text: "The beauty of everyday life lies in the details—quiet mornings, laughter-filled afternoons, and candid interactions. My approach is to preserve those fleeting moments as they naturally unfold, resulting in photographs that feel warm, real, and uniquely yours.",
    reverse: false,
  },
  {
    title: "Creative Portraits",
    text: "Art and imagination intertwine to create something extraordinary. Playing with light, color, and perspective, I craft images that go beyond the expected—pieces that are expressive, bold, and deeply personal.",
    reverse: true,
  },
  {
    title: "Nature",
    text: "From sweeping landscapes to delicate details, I seek to showcase the untamed beauty of the world around us. Each image is a celebration of texture, light, and atmosphere, highlighting the harmony and drama found in the natural environment.",
    reverse: false,
  },
];

function Portfolio() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);

  function handleWindowSizeChange() {
    setIsMobile(window.innerWidth <= 1200);
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  return (
    <Wrapper style={{ paddingTop: isMobile ? "0vh" : "10vh" }}>
      <Hero>
        <HeroSurface>
          <Aura $size="680px" $opacity={0.24} $left="-120px" $top="-120px" />
          <Aura $size="540px" $opacity={0.2} $right="-60px" $top="-40px" />
          <HeroContent>
            <Eyebrow>portfolio</Eyebrow>
            <HeroTitle>Stories with glow, intention, and movement.</HeroTitle>
            <HeroText>
              A curated look at the sessions I love—crafted to feel polished,
              modern, and softly boho without slowing the experience down.
            </HeroText>
          </HeroContent>
        </HeroSurface>
        <HeroBase />
      </Hero>

      <SectionGrid>
        {sections.map(({ title, text }, i) => (
          <MotionSlide key={title}>
            <SectionCard>
              <CardHeader>
                <CardIndex>{String(i + 1).padStart(2, "0")}</CardIndex>
                <CardTitle>{title}</CardTitle>
              </CardHeader>
              <SliderShell>
                <PhotoSlider header={title} />
              </SliderShell>
              <MotionFade>
                <CardText>{text}</CardText>
              </MotionFade>
              <Divider />
              <MetaRow>
                <MetaPill>artful light</MetaPill>
                <MetaPill>quiet movement</MetaPill>
                <MetaPill>honest story</MetaPill>
              </MetaRow>
            </SectionCard>
          </MotionSlide>
        ))}
      </SectionGrid>

      <LogoBanner>
        <AccentRing />
        <Column
          style={{ width: isMobile ? "90vw" : "32vw", textAlign: "center" }}
        >
          <LogoContainer>
            <img src={logo} alt="logo" loading="lazy" decoding="async" />
          </LogoContainer>
          <CTA>
            Ready to craft the next gallery together? Let’s design the story you
            want to live inside.
          </CTA>
        </Column>
      </LogoBanner>
    </Wrapper>
  );
}

export default Portfolio;

// 🔹 Motion Components
const MotionSlide = ({ children }) => (
  <motion.section
    initial={{ opacity: 0, y: 80 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    style={{
      display: "flex",
      justifyContent: "center",
      margin: "0 auto 20px",
      width: "100%",
    }}
  >
    {children}
  </motion.section>
);

const MotionFade = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, x: 100 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

// 🔹 Styled Components
const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  background: radial-gradient(
      circle at 12% 18%,
      rgba(204, 192, 171, 0.26),
      transparent 38%
    ),
    radial-gradient(
      circle at 88% 8%,
      rgba(176, 191, 170, 0.28),
      transparent 34%
    ),
    linear-gradient(180deg, #ada98d 0%, #f4efe5 48%, #f8f3ea 100%);
  overflow-x: hidden;
  width: 100%;
  min-height: 100vh;
`;

const Column = styled.section`
  display: flex;
  flex-direction: column;
  padding: ${({ $pad }) => $pad};
  background: ${({ $bg }) => $bg || "transparent"};
  margin-top: ${({ mt }) => mt || "0"};
`;

const Hero = styled.section`
  position: relative;
  width: 100%;
  padding: 80px 4vw 0;
  background: linear-gradient(160deg, #ada98d, rgba(227, 234, 224, 0.94));
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 70px 4vw 0;
  }
`;

const HeroSurface = styled.div`
  position: relative;
  max-width: 1180px;
  margin: 0 auto;
  padding: 70px clamp(28px, 4vw, 42px) 120px;
  background: linear-gradient(
    150deg,
    rgba(255, 255, 255, 0.9),
    rgba(245, 240, 232, 0.9)
  );
  border: 1px solid rgba(112, 124, 109, 0.12);
  border-bottom: none;
  border-radius: 28px 28px 0 0;
  box-shadow: 0 24px 80px rgba(37, 45, 38, 0.12);
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 22px 100px;
  }
`;

const HeroBase = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -120px;
  height: 160px;
  width: calc(100% - 8vw);
  max-width: 1180px;
  margin: 0 auto;
  background: linear-gradient(
    180deg,
    rgba(245, 240, 232, 0.92),
    rgba(245, 238, 227, 0.7)
  );
  border: 1px solid rgba(112, 124, 109, 0.1);
  border-top: none;
  border-radius: 0 0 28px 28px;
  box-shadow: 0 10px 60px rgba(37, 45, 38, 0.14);
  z-index: 1;
`;

const Aura = styled.span`
  position: absolute;
  width: ${({ $size }) => $size || "520px"};
  height: ${({ $size }) => $size || "520px"};
  background: radial-gradient(
    circle,
    rgba(204, 186, 160, 0.32),
    transparent 60%
  );
  filter: blur(28px);
  opacity: ${({ $opacity }) => $opacity || 0.2};
  z-index: 1;
  ${({ $left }) => $left && `left: ${$left};`}
  ${({ $right }) => $right && `right: ${$right};`}
  ${({ $top }) => $top && `top: ${$top};`}
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 760px;
  display: grid;
  gap: 14px;
`;

const Eyebrow = styled.span`
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-size: 0.8rem;
  color: rgba(61, 67, 57, 0.7);
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.5rem, 4vw, 3.6rem);
  color: #1f2722;
  margin: 0;
  font-weight: 600;
  line-height: 1.1;
`;

const HeroText = styled.p`
  color: rgba(38, 45, 38, 0.82);
  font-size: 1.05rem;
  max-width: 520px;
  margin: 8px 0 0;
  line-height: 1.6;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: clamp(22px, 3vw, 30px);
  padding: clamp(30px, 5vw, 50px) clamp(18px, 5vw, 28px) 70px;
  margin: -80px auto 0;
  position: relative;
  z-index: 3;
  max-width: 1180px;

  &:before {
    content: "";
    position: absolute;
    inset: 18px 0 -26px;
    background: linear-gradient(140deg, #ada98d, rgba(245, 240, 232, 0.94));
    border: 1px solid rgba(112, 124, 109, 0.12);
    border-radius: 26px;
    z-index: -1;
    box-shadow: 0 22px 70px rgba(37, 45, 38, 0.12);
  }

  @media (max-width: 768px) {
    margin-top: -40px;
    display: flex;
    flex-direction: column;
  }
`;

const SectionCard = styled.article`
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.9),
    rgba(249, 244, 235, 0.9)
  );
  border: 1px solid rgba(112, 124, 109, 0.14);
  border-radius: 22px;
  padding: 22px clamp(16px, 3vw, 26px) 26px;
  box-shadow: 0 18px 70px rgba(37, 45, 38, 0.12);
  backdrop-filter: blur(8px);
  display: grid;
  gap: 14px;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 20% 10%,
      rgba(189, 175, 149, 0.18),
      transparent 40%
    );
    opacity: 0.9;
    pointer-events: none;
  }

  @media (hover: hover) {
    transition: transform 0.28s ease, border-color 0.28s ease,
      box-shadow 0.28s ease;

    &:hover {
      transform: translateY(-6px);
      border-color: rgba(112, 124, 109, 0.3);
      box-shadow: 0 24px 80px rgba(37, 45, 38, 0.16);
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1;
`;

const CardIndex = styled.span`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(112, 124, 109, 0.2);
  color: #253028;
  display: grid;
  place-items: center;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
`;

const CardTitle = styled.h2`
  margin: 0;
  color: #1f2722;
  font-size: 1.5rem;
  letter-spacing: 0.01em;
`;

const SliderShell = styled.div`
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(112, 124, 109, 0.12);
  background: rgba(255, 255, 255, 0.65);
  z-index: 1;
`;

const CardText = styled.p`
  color: rgba(35, 43, 37, 0.9);
  margin: 4px 0 2px;
  line-height: 1.6;
  font-size: 1rem;
  z-index: 1;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(61, 67, 57, 0.25),
    transparent
  );
  margin: 6px 0;
  z-index: 1;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  z-index: 1;
`;

const MetaPill = styled.span`
  padding: 8px 12px;
  border-radius: 14px;
  background: rgba(113, 124, 109, 0.12);
  color: #1f2722;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: 1px solid rgba(112, 124, 109, 0.24);
`;

const LogoBanner = styled.section`
  background: linear-gradient(
    135deg,
    rgba(232, 225, 214, 0.72),
    rgba(240, 235, 226, 0.9)
  );
  border-top: 1px solid rgba(112, 124, 109, 0.12);
  border-bottom: 1px solid rgba(112, 124, 109, 0.12);
  padding: 70px 18px 90px;
  display: grid;
  place-items: center;
  position: relative;
  overflow: hidden;
`;

const AccentRing = styled.span`
  position: absolute;
  width: 520px;
  height: 520px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(180, 193, 176, 0.3),
    transparent 55%
  );
  filter: blur(18px);
  opacity: 0.6;
  z-index: 1;
`;

const LogoContainer = styled.div`
  padding: 10px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: 1px solid rgba(112, 124, 109, 0.18);
  margin: 0 auto 18px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  position: relative;
  z-index: 2;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px solid rgba(112, 124, 109, 0.24);
  }
`;

const CTA = styled.p`
  color: rgba(35, 43, 37, 0.92);
  margin: 0;
  font-size: 1rem;
  line-height: 1.6;
  z-index: 2;
`;
